<!--
   The MIT License

   Copyright (c) 2019- Nordic Institute for Interoperability Solutions (NIIS)
   Copyright (c) 2018 Estonian Information System Authority (RIA),
   Nordic Institute for Interoperability Solutions (NIIS), Population Register Centre (VRK)
   Copyright (c) 2015-2017 Estonian Information System Authority (RIA), Population Register Centre (VRK)

   Permission is hereby granted, free of charge, to any person obtaining a copy
   of this software and associated documentation files (the "Software"), to deal
   in the Software without restriction, including without limitation the rights
   to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
   copies of the Software, and to permit persons to whom the Software is
   furnished to do so, subject to the following conditions:

   The above copyright notice and this permission notice shall be included in
   all copies or substantial portions of the Software.

   THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
   IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
   FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
   AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
   LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
   OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
   THE SOFTWARE.
 -->
<template>
  <article id="download-url" class="mt-5">
    <v-data-table
      v-if="urls"
      :headers="headers"
      :items="urls"
      :items-per-page="-1"
      :loading="loading"
      item-key="url"
      hide-default-footer
      class="elevation-0 data-table"
    >
      <template #top>
        <data-table-toolbar title-key="globalConf.downloadUrl.title" />
      </template>
      <template #[`item.url`]="{ item }">
        <a
          class="xrd-clickable"
          target="_blank"
          rel="noreferrer"
          :href="item.url"
        >
          <v-icon class="internal-conf-icon" icon="mdi-link" />
          {{ item.url }}
        </a>
      </template>
      <template #bottom>
        <XrdDataTableFooter />
      </template>
    </v-data-table>
  </article>
</template>

<script lang="ts">
/**
 * View for 'backup and restore' tab
 */
import { defineComponent, PropType } from 'vue';
import { mapStores } from 'pinia';
import { ConfigurationType, GlobalConfDownloadUrl } from '@/openapi-types';
import { useConfigurationSource } from '@/store/modules/configuration-sources';
import { DataTableHeader } from '@/ui-types';
import { XrdDataTableFooter } from '@niis/shared-ui';
import DataTableToolbar from '@/components/ui/DataTableToolbar.vue';

export default defineComponent({
  components: { XrdDataTableFooter, DataTableToolbar },
  props: {
    configurationType: {
      type: String as PropType<ConfigurationType>,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
    };
  },
  computed: {
    ...mapStores(useConfigurationSource),
    urls(): GlobalConfDownloadUrl[] {
      return [
        this.configurationSourceStore.getDownloadUrl(this.configurationType),
      ];
    },
    headers(): DataTableHeader[] {
      return [
        {
          title: this.$t('globalConf.downloadUrl.urlAddress') as string,
          align: 'start',
          key: 'url',
        },
      ];
    },
  },
  created() {
    this.fetchDownloadUrl();
  },
  methods: {
    fetchDownloadUrl() {
      this.loading = true;
      this.configurationSourceStore
        .fetchDownloadUrl(this.configurationType)
        .finally(() => (this.loading = false));
    },
    openInNewTab(url: string) {
      window.open(url, '_blank', 'noreferrer');
    },
  },
});
</script>

<style lang="scss" scoped>
@use '@niis/shared-ui/src/assets/colors';
@use '@niis/shared-ui/src/assets/tables' as *;

.internal-conf-icon {
  margin-right: 15px;
  color: colors.$Purple100;
}

.xrd-clickable {
  text-decoration-line: none;
}
</style>
